import React, { useEffect, useRef, useState, useCallback } from 'react';
import './StyleDNA.css';

// Extract style attributes from the image using canvas color analysis
function analyzeImage(img) {
  const canvas = document.createElement('canvas');
  const size = 80;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, size, size);
  const data = ctx.getImageData(0, 0, size, size).data;

  let totalR = 0, totalG = 0, totalB = 0;
  let bright = 0, dark = 0, saturated = 0, muted = 0;
  let edgeEnergy = 0;
  const n = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    totalR += r; totalG += g; totalB += b;

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    if (luminance > 0.6) bright++;
    if (luminance < 0.3) dark++;

    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;
    if (saturation > 0.4) saturated++;
    else muted++;

    // Simple edge detection: compare to previous pixel
    if (i > 4) {
      const pr = data[i - 4], pg = data[i - 3], pb = data[i - 2];
      edgeEnergy += Math.abs(r - pr) + Math.abs(g - pg) + Math.abs(b - pb);
    }
  }

  const avgR = totalR / n;
  const avgG = totalG / n;
  const avgB = totalB / n;

  // Normalize metrics to 0–1
  const brightness = bright / n;
  const darkness = dark / n;
  const saturationRatio = saturated / n;
  const complexity = Math.min(1, edgeEnergy / (n * 100));
  const warmth = (avgR - avgB + 255) / 510; // 0 = cold blue, 1 = warm red
  const vibrancy = saturationRatio;
  const moodDark = darkness;

  return {
    Energy:     Math.round(Math.min(100, complexity * 200)),
    Vibrancy:   Math.round(vibrancy * 100),
    Warmth:     Math.round(warmth * 100),
    Brightness: Math.round(brightness * 100),
    Mystique:   Math.round(moodDark * 100),
  };
}

// Draw an animated radar chart on a canvas
function drawRadar(canvas, labels, values, progress, accentColor) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const cx = W / 2, cy = H / 2;
  const sides = labels.length;
  const R = Math.min(W, H) / 2 - 24;

  ctx.clearRect(0, 0, W, H);

  // Grid rings
  for (let ring = 1; ring <= 4; ring++) {
    const r = (R * ring) / 4;
    ctx.beginPath();
    for (let i = 0; i < sides; i++) {
      const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.strokeStyle = 'rgba(139, 92, 246, 0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Spokes
  for (let i = 0; i < sides; i++) {
    const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + R * Math.cos(angle), cy + R * Math.sin(angle));
    ctx.strokeStyle = 'rgba(139, 92, 246, 0.15)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Data polygon (animated with progress 0→1)
  const animatedValues = values.map(v => v * progress);
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
    const r = (animatedValues[i] / 100) * R;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
  // Gradient fill
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
  grad.addColorStop(0, accentColor + '60');
  grad.addColorStop(1, accentColor + '15');
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = accentColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Data points
  for (let i = 0; i < sides; i++) {
    const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
    const r = (animatedValues[i] / 100) * R;
    const x = cx + r * Math.cos(angle);
    const y = cy + r * Math.sin(angle);
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, 2 * Math.PI);
    ctx.fillStyle = accentColor;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // Labels
  ctx.font = '600 11px Inter, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let i = 0; i < sides; i++) {
    const angle = (2 * Math.PI * i) / sides - Math.PI / 2;
    const labelR = R + 18;
    const x = cx + labelR * Math.cos(angle);
    const y = cy + labelR * Math.sin(angle);
    ctx.fillStyle = 'rgba(245, 245, 245, 0.85)';
    ctx.fillText(labels[i], x, y);
  }
}

// Accent color per dominant attribute
const ACCENT_BY_ATTR = {
  Energy:     '#f97316',
  Vibrancy:   '#8b5cf6',
  Warmth:     '#ef4444',
  Brightness: '#facc15',
  Mystique:   '#6366f1',
};

export default function StyleDNA({ imageUrl }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const progressRef = useRef(0);
  const startTimeRef = useRef(null);

  const analyze = useCallback(() => {
    if (!imageUrl) return;
    setLoading(true);
    setError(null);
    setMetrics(null);
    progressRef.current = 0;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const result = analyzeImage(img);
        setMetrics(result);
        setLoading(false);
      } catch (e) {
        setError('Could not analyze image.');
        setLoading(false);
      }
    };
    img.onerror = () => {
      setError('Could not load image for analysis.');
      setLoading(false);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    analyze();
  }, [analyze]);

  // Animate radar when metrics available
  useEffect(() => {
    if (!metrics || !canvasRef.current || !expanded) return;
    const labels = Object.keys(metrics);
    const values = Object.values(metrics);
    const dominant = labels.reduce((a, b) => metrics[a] > metrics[b] ? a : b);
    const accent = ACCENT_BY_ATTR[dominant] || '#8b5cf6';

    progressRef.current = 0;
    startTimeRef.current = null;

    const animate = (ts) => {
      if (!startTimeRef.current) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const duration = 900;
      progressRef.current = Math.min(1, elapsed / duration);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progressRef.current, 3);
      drawRadar(canvasRef.current, labels, values, eased, accent);
      if (progressRef.current < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [metrics, expanded]);

  if (!imageUrl) return null;

  const labels = metrics ? Object.keys(metrics) : [];
  const values = metrics ? Object.values(metrics) : [];
  const dominant = metrics ? labels.reduce((a, b) => metrics[a] > metrics[b] ? a : b) : null;

  return (
    <div className="style-dna-card fade-in">
      <button
        className="style-dna-header"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <span className="style-dna-title">
          <span className="style-dna-icon">🧬</span>
          Style DNA
        </span>
        <span className="style-dna-chevron">{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div className="style-dna-body">
          {loading && (
            <div className="style-dna-loading">
              <div className="style-dna-spinner" />
              <span>Analyzing design…</span>
            </div>
          )}
          {error && (
            <div className="style-dna-error">{error}</div>
          )}
          {metrics && (
            <>
              <div className="style-dna-canvas-wrap">
                <canvas
                  ref={canvasRef}
                  width={200}
                  height={200}
                  className="style-dna-canvas"
                />
              </div>
              <div className="style-dna-bars">
                {labels.map((label, i) => (
                  <div key={label} className="style-dna-bar-row">
                    <span className="style-dna-bar-label">{label}</span>
                    <div className="style-dna-bar-track">
                      <div
                        className="style-dna-bar-fill"
                        style={{
                          width: `${values[i]}%`,
                          background: ACCENT_BY_ATTR[label] || '#8b5cf6',
                          animationDelay: `${i * 80}ms`,
                        }}
                      />
                    </div>
                    <span className="style-dna-bar-value">{values[i]}</span>
                  </div>
                ))}
              </div>
              {dominant && (
                <div className="style-dna-dominant">
                  <span className="style-dna-dominant-label">Dominant trait:</span>
                  <span
                    className="style-dna-dominant-value"
                    style={{ color: ACCENT_BY_ATTR[dominant] || '#8b5cf6' }}
                  >
                    {dominant}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
